import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Graph, Plus, ClockCounterClockwise, LinkBreak, ArrowSquareOut, Lock, Question,
  Robot, WarningCircle, MagnifyingGlass, TreeStructure, Sparkle, Check, X, ArrowRight,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatJST } from '@/utils/datetime';
import { liveUrl } from '@/utils/retiredRoutes';
import { traceApi, traceConfig } from './traceApi';

function AddLinkDialog({ open, onClose, entityType, entityId, config, onDone }) {
  const [ttype, setTtype] = useState('');
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [relation, setRelation] = useState('related_to');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setTtype(''); setQ(''); setResults([]); setPicked(null); setRelation('related_to'); setNote(''); }
  }, [open]);

  useEffect(() => {
    if (!open || !ttype) return undefined;
    const t = setTimeout(() => {
      traceApi.search(ttype, q).then(setResults).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, ttype, open]);

  const save = async () => {
    if (!picked) { toast.error('リンク先のレコードを選択してください'); return; }
    setBusy(true);
    try {
      await traceApi.createLink({
        source_type: entityType, source_id: entityId,
        target_type: ttype, target_id: picked.id,
        relation_type: relation, note: note.trim() || null,
      });
      toast.success('関連レコードをリンクしました');
      onDone(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'リンクに失敗しました');
    } finally { setBusy(false); }
  };

  const types = (config?.entity_types || []).filter((t) => !(t.id === entityType));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="trace-add-dialog">
        <DialogHeader><DialogTitle className="text-base">関連レコードを追加</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-zinc-600">リンク先の種類</label>
            <Select value={ttype} onValueChange={(v) => { setTtype(v); setPicked(null); setResults([]); }}>
              <SelectTrigger className="h-9 mt-1 text-sm" data-testid="trace-add-module"><SelectValue placeholder="モジュールを選択" /></SelectTrigger>
              <SelectContent>
                {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.label_ja}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {ttype && (
            <div>
              <label className="text-xs font-semibold text-zinc-600">レコードを検索</label>
              <div className="relative mt-1">
                <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="タイトル・番号で検索…"
                  className="h-9 pl-8 text-sm" data-testid="trace-add-search" autoFocus />
              </div>
              <div className="mt-1.5 max-h-44 overflow-y-auto space-y-1">
                {results.length === 0 && <p className="text-[11px] text-zinc-400 py-2 text-center">該当するレコードがありません</p>}
                {results.map((r) => (
                  <button key={r.id} type="button" onClick={() => setPicked(r)}
                    className={`w-full flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left transition-colors ${
                      picked?.id === r.id ? 'border-indigo-400 bg-indigo-50' : 'border-zinc-200 hover:bg-zinc-50'}`}
                    data-testid={`trace-add-item-${r.id}`}>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] font-medium text-zinc-800 truncate">{r.title}</span>
                      {r.subtitle && <span className="block text-[10px] text-zinc-400 truncate">{r.subtitle}</span>}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold text-zinc-600">関連の種類</label>
              <Select value={relation} onValueChange={setRelation}>
                <SelectTrigger className="h-9 mt-1 text-sm" data-testid="trace-add-relation"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(config?.link_types || []).map((lt) => <SelectItem key={lt.code} value={lt.code}>{lt.label_ja}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-zinc-600">メモ（任意）</label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="リンク理由など"
                className="h-9 mt-1 text-sm" data-testid="trace-add-note" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>キャンセル</Button>
          <Button onClick={save} disabled={busy || !picked} className="bg-teal-700 hover:bg-teal-800" data-testid="trace-add-save">
            {busy ? '保存中…' : 'リンクを保存'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog({ open, onClose, onConfirm, suggestion }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setReason(''); setBusy(false); } }, [open]);
  const confirm = async () => {
    setBusy(true);
    try { await onConfirm(reason.trim() || null); onClose(); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" aria-describedby={undefined} data-testid="trace-reject-dialog">
        <DialogHeader><DialogTitle className="text-base">AI候補を拒否</DialogTitle></DialogHeader>
        {suggestion && (
          <p className="text-[12px] text-zinc-500">
            <span className="text-zinc-700 font-medium">{suggestion.target_label}</span>
            <span className="text-zinc-400"> — {suggestion.reason_ja}</span>
          </p>
        )}
        <div>
          <label className="text-xs font-semibold text-zinc-600">拒否理由（任意）</label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="なぜ関連しないかを記録できます"
            className="h-9 mt-1 text-sm" data-testid="trace-reject-reason" autoFocus />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>キャンセル</Button>
          <Button onClick={confirm} disabled={busy}
            className="bg-rose-600 hover:bg-rose-700" data-testid="trace-reject-confirm">
            {busy ? '拒否中…' : '拒否する'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LinkedItem({ item, onUnlink, canUnlink }) {
  const navigate = useNavigate();
  const [whyOpen, setWhyOpen] = useState(false);
  if (item.restricted) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-dashed border-zinc-300 bg-zinc-50 px-2.5 py-2"
        data-testid={`trace-restricted-${item.link_id}`}>
        <Lock size={15} className="text-zinc-400 shrink-0" />
        <span className="text-[12px] text-zinc-400">権限により非表示の関連レコード</span>
        <Badge variant="outline" className="text-[9px] ml-auto text-zinc-400">{item.relation_label_ja}</Badge>
      </div>
    );
  }
  if (item.missing || !item.record) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-dashed border-zinc-200 bg-zinc-50/60 px-2.5 py-2"
        data-testid={`trace-missing-${item.link_id}`}>
        <WarningCircle size={15} className="text-zinc-300 shrink-0" />
        <span className="text-[12px] text-zinc-400">リンク先のレコードが見つかりません（削除された可能性があります）</span>
        {canUnlink && (
          <button type="button" onClick={() => onUnlink(item)} className="ml-auto text-zinc-300 hover:text-rose-500" data-testid={`trace-unlink-${item.link_id}`}>
            <LinkBreak size={14} />
          </button>
        )}
      </div>
    );
  }
  const rec = item.record;
  const openUrl = liveUrl(rec.url);
  return (
    <div className="rounded-lg border border-zinc-200 bg-white px-2.5 py-2" data-testid={`trace-item-${item.link_id}`}>
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => openUrl && navigate(openUrl)} disabled={!openUrl}
          className="min-w-0 flex-1 text-left group" data-testid={`trace-open-${item.link_id}`}>
          <span className="block text-[13px] font-medium text-zinc-800 truncate group-hover:text-indigo-700 group-hover:underline">
            {rec.title} {openUrl && <ArrowSquareOut size={11} className="inline text-zinc-300 group-hover:text-indigo-500" />}
          </span>
          {rec.subtitle && <span className="block text-[10px] text-zinc-400 truncate">{rec.subtitle}</span>}
        </button>
        <Badge variant="outline" className="text-[9px] shrink-0 text-indigo-600 border-indigo-200">
          {item.relation_label_ja}{item.direction === 'in' ? '（被リンク）' : ''}
        </Badge>
        {item.is_auto_created && (
          <button type="button" onClick={() => setWhyOpen((v) => !v)} title="リンク理由"
            className="shrink-0 inline-flex items-center gap-0.5 text-[9px] font-semibold text-violet-600 bg-violet-50 border border-violet-200 rounded-full px-1.5 py-0.5 hover:bg-violet-100"
            data-testid={`trace-why-${item.link_id}`}>
            <Robot size={11} /> 自動 <Question size={10} />
          </button>
        )}
        {canUnlink && (
          <button type="button" onClick={() => onUnlink(item)} title="リンクを解除"
            className="text-zinc-300 hover:text-rose-500 transition-colors shrink-0" data-testid={`trace-unlink-${item.link_id}`}>
            <LinkBreak size={15} />
          </button>
        )}
      </div>
      {whyOpen && item.why_linked_ja && (
        <p className="mt-1.5 text-[11px] text-violet-700 bg-violet-50 rounded-md px-2 py-1.5" data-testid={`trace-why-text-${item.link_id}`}>
          <span className="font-semibold">リンク理由: </span>{item.why_linked_ja}
        </p>
      )}
      <p className="mt-1 text-[10px] text-zinc-400">
        {item.note && <span className="text-zinc-500">{item.note} · </span>}
        {item.created_by_name} · {formatJST(item.created_at)}
      </p>
    </div>
  );
}

function GraphView({ entityType, entityId }) {
  const navigate = useNavigate();
  const [depth, setDepth] = useState(2);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!entityId) return;
    setLoading(true);
    traceApi.graph(entityType, entityId, depth)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [entityType, entityId, depth]);

  // Build hop-grouped view: hop0 → hop1 → hop2 → hop3
  const grouped = useMemo(() => {
    if (!data?.nodes) return [];
    const byHop = {};
    for (const n of data.nodes) {
      if (n.hop === 0) continue; // skip the root
      (byHop[n.hop] = byHop[n.hop] || []).push(n);
    }
    // sort each group by type_label then title
    for (const h of Object.keys(byHop)) {
      byHop[h].sort((a, b) => {
        const ta = (a.type_label || '').localeCompare(b.type_label || '', 'ja');
        if (ta !== 0) return ta;
        return (a.title || '').localeCompare(b.title || '', 'ja');
      });
    }
    return Object.keys(byHop).sort().map((h) => ({ hop: Number(h), nodes: byHop[h] }));
  }, [data]);

  return (
    <div className="space-y-2" data-testid="trace-graph-view">
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-zinc-500">深さ:</span>
        <div className="inline-flex rounded-lg bg-zinc-100 p-0.5 text-[11px]">
          {[1, 2, 3].map((d) => (
            <button key={d} type="button" onClick={() => setDepth(d)}
              data-testid={`trace-graph-depth-${d}`}
              className={`px-2 py-0.5 rounded-md font-medium transition-colors ${
                depth === d ? 'bg-white text-indigo-700 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'}`}>
              {d}ホップ
            </button>
          ))}
        </div>
        {data && !loading && (
          <span className="text-[10px] text-zinc-400 ml-auto tabular-nums" data-testid="trace-graph-stats">
            ノード {data.nodes?.length || 0} / エッジ {data.edges?.length || 0}
            {data.truncated ? '（省略）' : ''}
          </span>
        )}
      </div>
      {loading && <p className="text-[11px] text-zinc-400 py-2">読み込み中…</p>}
      {!loading && data && grouped.length === 0 && (
        <p className="text-[11px] text-zinc-400" data-testid="trace-graph-empty">
          このレコードから辿れる関連レコードはありません
        </p>
      )}
      {!loading && grouped.map((g) => (
        <div key={g.hop} className="space-y-1" data-testid={`trace-graph-hop-${g.hop}`}>
          <p className="text-[10px] font-bold uppercase tracking-wide text-zinc-400">
            {g.hop}ホップ先（{g.nodes.length}件）
          </p>
          <div className="space-y-1">
            {g.nodes.map((n) => (
              <div key={n.key}
                className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 ${
                  n.restricted ? 'border-dashed border-zinc-300 bg-zinc-50' :
                  n.missing ? 'border-dashed border-zinc-200 bg-zinc-50/60' :
                  'border-zinc-200 bg-white'}`}
                style={{ marginLeft: `${(g.hop - 1) * 12}px` }}
                data-testid={`trace-graph-node-${n.type}-${n.id}`}>
                <Badge variant="outline" className="text-[9px] shrink-0">{n.type_label}</Badge>
                {n.restricted ? (
                  <span className="text-[12px] text-zinc-400 flex items-center gap-1">
                    <Lock size={12} /> 権限により非表示
                  </span>
                ) : n.missing ? (
                  <span className="text-[12px] text-zinc-400">レコード不明</span>
                ) : (
                  <button type="button" onClick={() => liveUrl(n.url) && navigate(liveUrl(n.url))}
                    disabled={!liveUrl(n.url)}
                    className="min-w-0 flex-1 text-left group">
                    <span className="block text-[13px] font-medium text-zinc-800 truncate group-hover:text-indigo-700 group-hover:underline">
                      {n.title} {liveUrl(n.url) && <ArrowSquareOut size={10} className="inline text-zinc-300 group-hover:text-indigo-500" />}
                    </span>
                    {n.subtitle && <span className="block text-[10px] text-zinc-400 truncate">{n.subtitle}</span>}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function AiSuggestionCard({ suggestion, onApprove, onReject }) {
  const navigate = useNavigate();
  const conf = Math.round((suggestion.confidence || 0) * 100);
  const tone = conf >= 75 ? 'emerald' : conf >= 50 ? 'amber' : 'zinc';
  const toneClasses = {
    emerald: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    amber: 'text-amber-700 bg-amber-50 border-amber-200',
    zinc: 'text-zinc-600 bg-zinc-50 border-zinc-200',
  }[tone];
  return (
    <div className="rounded-lg border border-violet-200 bg-white px-2.5 py-2 space-y-1.5"
      data-testid={`trace-suggestion-${suggestion.id}`}>
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="text-[9px] shrink-0 text-violet-600 border-violet-200 bg-white inline-flex items-center gap-0.5">
          <Sparkle size={10} weight="fill" /> AI提案
        </Badge>
        <Badge variant="outline" className="text-[9px] shrink-0">{suggestion.target_type_label}</Badge>
        <button type="button"
          onClick={() => suggestion.target_url && navigate(suggestion.target_url)}
          className="min-w-0 flex-1 text-left group"
          data-testid={`trace-suggestion-open-${suggestion.id}`}>
          <span className="block text-[13px] font-medium text-zinc-800 truncate group-hover:text-indigo-700 group-hover:underline">
            {suggestion.target_label} <ArrowSquareOut size={10} className="inline text-zinc-300 group-hover:text-indigo-500" />
          </span>
          {suggestion.target_subtitle && (
            <span className="block text-[10px] text-zinc-400 truncate">{suggestion.target_subtitle}</span>
          )}
        </button>
        <span className={`text-[10px] font-semibold rounded-full px-1.5 py-0.5 border ${toneClasses}`}
          data-testid={`trace-suggestion-conf-${suggestion.id}`}>{conf}%</span>
      </div>
      <p className="text-[11px] text-violet-800 leading-snug">
        <span className="font-semibold">理由: </span>{suggestion.reason_ja}
      </p>
      <div className="flex items-center gap-1.5">
        <Badge variant="outline" className="text-[9px] text-indigo-600 border-indigo-200">
          {suggestion.relation_label_ja}
        </Badge>
        <Button size="sm" onClick={() => onApprove(suggestion)}
          className="h-6 text-[11px] px-2 bg-teal-700 hover:bg-teal-800 ml-auto"
          data-testid={`trace-suggestion-approve-${suggestion.id}`}>
          <Check size={12} className="mr-1" /> 承認
        </Button>
        <Button size="sm" variant="outline" onClick={() => onReject(suggestion)}
          className="h-6 text-[11px] px-2 text-rose-600 border-rose-200 hover:bg-rose-50"
          data-testid={`trace-suggestion-reject-${suggestion.id}`}>
          <X size={12} className="mr-1" /> 拒否
        </Button>
      </div>
    </div>
  );
}

function AiSuggestSection({ entityType, entityId, onLinkAdded }) {
  const [items, setItems] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);

  const reload = useCallback(() => {
    if (!entityId) return;
    traceApi.listSuggestions(entityType, entityId, 'pending')
      .then((r) => setItems(r?.suggestions || []))
      .catch(() => setItems([]));
  }, [entityType, entityId]);
  useEffect(() => { reload(); }, [reload]);

  const scan = async () => {
    setScanning(true);
    try {
      const r = await traceApi.suggest(entityType, entityId, 5);
      const created = r?.created || 0;
      if (created > 0) toast.success(`AIが ${created} 件の関連候補を提案しました`);
      else toast.info(r?.reason || '追加の関連候補は見つかりませんでした');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI候補の取得に失敗しました');
    } finally { setScanning(false); }
  };

  const approve = async (s) => {
    try {
      await traceApi.approveSuggestion(s.id);
      toast.success('AI候補を承認してリンクを作成しました');
      reload();
      if (onLinkAdded) onLinkAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '承認に失敗しました');
      reload();
    }
  };

  const reject = async (reason) => {
    if (!rejectTarget) return;
    try {
      await traceApi.rejectSuggestion(rejectTarget.id, reason);
      toast.success('AI候補を拒否しました');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '拒否に失敗しました');
    }
  };

  return (
    <div className="rounded-xl border border-violet-200 bg-violet-50/40 p-2.5 space-y-2" data-testid="trace-ai-suggest">
      <div className="flex items-center gap-2">
        <p className="text-xs font-semibold text-violet-700 flex items-center gap-1.5">
          <Sparkle size={14} weight="fill" className="text-violet-600" /> AIリンク候補
          {items.length > 0 && (
            <span className="text-[10px] font-bold text-violet-700 tabular-nums" data-testid="trace-ai-count">
              {items.length}
            </span>
          )}
        </p>
        <Button size="sm" variant="outline" onClick={scan} disabled={scanning}
          className="ml-auto h-7 text-[11px] px-2 text-violet-700 border-violet-200 hover:bg-violet-100"
          data-testid="trace-ai-scan">
          {scanning ? 'AIが検索中…' : 'AIで候補を探す'}
        </Button>
      </div>
      {items.length === 0 ? (
        <p className="text-[11px] text-zinc-400" data-testid="trace-ai-empty">
          「AIで候補を探す」を押すと、意味的に関連しそうなレコードをAIが提案します
        </p>
      ) : (
        <div className="space-y-1.5">
          {items.map((s) => (
            <AiSuggestionCard key={s.id} suggestion={s}
              onApprove={approve} onReject={setRejectTarget} />
          ))}
        </div>
      )}
      <RejectDialog open={!!rejectTarget} onClose={() => setRejectTarget(null)}
        suggestion={rejectTarget} onConfirm={reject} />
    </div>
  );
}

export default function TraceabilityPanel({ entityType, entityId, className = '', readOnly = false }) {
  const [config, setConfig] = useState(null);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('related');
  const [timeline, setTimeline] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [showAllWarn, setShowAllWarn] = useState(false);

  useEffect(() => { traceConfig().then(setConfig); }, []);

  const load = useCallback(() => {
    if (!entityId) return;
    traceApi.links(entityType, entityId).then(setData).catch(() => setData(null));
    traceApi.warnings(entityType, entityId).then((w) => setWarnings(w?.warnings || [])).catch(() => setWarnings([]));
  }, [entityType, entityId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (tab !== 'timeline' || !entityId) return;
    traceApi.timeline(entityType, entityId).then(setTimeline).catch(() => setTimeline(null));
  }, [tab, entityType, entityId]);

  const moduleOn = config?.enabled && data?.enabled;
  if (!config || !moduleOn) return null;

  const unlink = async (item) => {
    if (!window.confirm('この関連リンクを解除しますか？（レコード自体は削除されません）')) return;
    try {
      await traceApi.deleteLink(item.link_id);
      toast.success('リンクを解除しました');
      load();
      if (tab === 'timeline') traceApi.timeline(entityType, entityId).then(setTimeline).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || '解除に失敗しました'); }
  };

  const groups = data?.groups || [];
  const total = groups.reduce((n, g) => n + g.items.length, 0);
  const visibleWarnings = showAllWarn ? warnings : warnings.slice(0, 2);

  return (
    <div className={`space-y-2 ${className}`} data-testid="trace-panel">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs font-semibold text-zinc-600 flex items-center gap-1.5">
          <Graph size={15} weight="bold" className="text-indigo-600" /> 関連レコード
          {total > 0 && <span className="text-[10px] font-bold text-indigo-600 tabular-nums" data-testid="trace-count">{total}</span>}
        </p>
        <div className="flex items-center gap-1 rounded-lg bg-zinc-100 p-0.5 text-[11px]">
          <button type="button" onClick={() => setTab('related')} data-testid="trace-tab-related"
            className={`px-2 py-1 rounded-md font-medium transition-colors ${tab === 'related' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'}`}>
            関連レコード
          </button>
          <button type="button" onClick={() => setTab('graph')} data-testid="trace-tab-graph"
            className={`px-2 py-1 rounded-md font-medium transition-colors inline-flex items-center gap-1 ${tab === 'graph' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'}`}>
            <TreeStructure size={12} /> 関連グラフ
          </button>
          <button type="button" onClick={() => setTab('timeline')} data-testid="trace-tab-timeline"
            className={`px-2 py-1 rounded-md font-medium transition-colors inline-flex items-center gap-1 ${tab === 'timeline' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'}`}>
            <ClockCounterClockwise size={12} /> 履歴タイムライン
          </button>
        </div>
        {!readOnly && (
          <Button size="sm" variant="outline" className="ml-auto h-7 text-[11px] px-2 text-indigo-700 border-indigo-200 hover:bg-indigo-50"
            onClick={() => setAddOpen(true)} data-testid="trace-add-link-btn">
            <Plus size={12} className="mr-1" /> 関連レコードを追加
          </Button>
        )}
      </div>

      {warnings.length > 0 && tab === 'related' && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 space-y-0.5" data-testid="trace-warnings">
          {visibleWarnings.map((w, i) => (
            <p key={i} className="text-[11px] text-amber-700 flex items-center gap-1.5">
              <WarningCircle size={13} className="shrink-0" /> {w}
            </p>
          ))}
          {warnings.length > 2 && (
            <button type="button" onClick={() => setShowAllWarn((v) => !v)} className="text-[10px] text-amber-600 underline">
              {showAllWarn ? '折りたたむ' : `他 ${warnings.length - 2} 件`}
            </button>
          )}
        </div>
      )}

      {tab === 'related' && (
        <>
          {total === 0 ? (
            <p className="text-[11px] text-zinc-400" data-testid="trace-empty">関連レコードはありません</p>
          ) : (
            <div className="space-y-2.5">
              {groups.map((g) => (
                <div key={g.type} data-testid={`trace-group-${g.type}`}>
                  <p className="text-[10px] font-bold uppercase tracking-wide text-zinc-400 mb-1">{g.label_ja}（{g.items.length}）</p>
                  <div className="space-y-1">
                    {g.items.map((item) => (
                      <LinkedItem key={item.link_id} item={item} onUnlink={unlink} canUnlink />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <AiSuggestSection entityType={entityType} entityId={entityId} onLinkAdded={load} />
        </>
      )}

      {tab === 'graph' && (
        <GraphView entityType={entityType} entityId={entityId} />
      )}

      {tab === 'timeline' && (
        <div className="space-y-1" data-testid="trace-timeline">
          {!timeline && <p className="text-[11px] text-zinc-400 py-2">読み込み中…</p>}
          {timeline && (timeline.events || []).length === 0 && (
            <p className="text-[11px] text-zinc-400" data-testid="trace-timeline-empty">履歴はまだありません</p>
          )}
          {(timeline?.events || []).map((ev) => (
            <div key={ev.id} className="flex items-start gap-2 rounded-lg bg-zinc-50 px-2.5 py-1.5 text-[11px]"
              data-testid={`trace-timeline-row-${ev.id}`}>
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-indigo-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <span className="font-medium text-zinc-700">{ev.action_label_ja}</span>
                {ev.related_label && <span className="text-zinc-500"> — {ev.related_label}</span>}
                {ev.note && <span className="text-zinc-400">（{ev.note}）</span>}
                <span className="block text-[10px] text-zinc-400">
                  {ev.actor_name} · {formatJST(ev.created_at)} · {ev.source_module}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      <AddLinkDialog open={addOpen} onClose={() => setAddOpen(false)}
        entityType={entityType} entityId={entityId} config={config} onDone={load} />
    </div>
  );
}
